var Type = /*#__PURE__*/function (Type) {
  Type["Array"] = "array";
  Type["AsyncFunction"] = "asyncfunction";
  Type["Error"] = "error";
  Type["Function"] = "function";
  Type["Null"] = "null";
  Type["Number"] = "number";
  Type["Object"] = "object";
  Type["Promise"] = "promise";
  Type["Regexp"] = "regexp";
  Type["String"] = "string";
  Type["Symbol"] = "symbol";
  Type["Undefined"] = "undefined";
  return Type;
}(Type || {});
/**
 * @remarks {@link typeOf typeOf()} returns the type of the given value.
 *
 * @param {unknown} value The value to check.
 * @returns array | function | null | number | object | promise | regexp | string | symbol | undefined
 *
 * @example
 *
 * ```ts
 * typeOf(''); // string
 * ```
 */
const typeOf = value => Object.prototype.toString.call(value).slice(8, -1).toLowerCase();

/**
 * @remarks {@link isArray isArray()} checks if a given value is an array.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isArray([]); // true
 * ```
 */
const isArray = value => typeOf(value) === Type.Array;

/**
 * @remarks {@link isAsyncFunction isAsyncFunction()} checks if a given value is an async function.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isAsyncFunction(() => {}); // false
 * isAsyncFunction(async () => {}); // true
 * ```
 */
const isAsyncFunction = value => typeOf(value) === Type.AsyncFunction;

/**
 * @remarks {@link isFunction isFunction()} checks if a given value is a function.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isFunction(() => {}); // true
 * isFunction(async () => {}); // true
 * ```
 */
const isFunction = value => typeOf(value).includes(Type.Function);

/**
 * @remarks {@link isError isError()} checks if a given value is an error.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isError(new Error(':())); // true
 * ```
 */
const isError = value => typeOf(value).includes(Type.Error);

/**
 * @remarks {@link isNull isNull()} checks if a given value is null.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isNull(null); // true
 * ```
 */
const isNull = value => typeOf(value) === Type.Null;

/**
 * @remarks {@link isNumber isNumber()} checks if a given value is a number.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isNumber(1); // true
 * ```
 */
const isNumber = value => typeOf(value) === Type.Number;

/**
 * @remarks {@link isObject isObject()} checks if a given value is an object.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isObject({}); // true
 * ```
 */
const isObject = value => typeOf(value) === Type.Object;

/**
 * @remarks {@link isPromise isPromise()} checks if a given value is a promise.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isPromise(Promise.resolve()); // true
 * ```
 */
const isPromise = value => typeOf(value) === Type.Promise;

/**
 * @remarks {@link isRegexp isRegexp()} checks if a given value is a regular expression.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isRegexp(/\/); // true
 * ```
 */
const isRegexp = value => typeOf(value) === Type.Regexp;

/**
 * @remarks {@link isString isString()} checks if a given value is a string.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isString(''); // true
 * ```
 */
const isString = value => typeOf(value) === Type.String;

/**
 * @remarks {@link isSymbol isSymbol()} checks if a given value is a symbol.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isSymbol(new Symbol()); // true
 * ```
 */
const isSymbol = value => typeOf(value) === Type.Symbol;

/**
 * @remarks {@link isUndefined isUndefined()} checks if a given value is undefined.
 *
 * @param {unknown} value The value to check.
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isUndefined(undefined); // true
 * ```
 */
const isUndefined = value => typeOf(value) === Type.Undefined;
/**
 * @remarks {@link isNil isNil()} checks if a given value is undefined or null.
 *
 * @param value - The value to check.
 *
 * @returns Whether or not the value is nil.
 *
 * @example
 *
 * ```ts
 * isNil(undefined); // true
 * isNil(null); // true
 * isNil('foo'); // false
 * isNil(123); // false
 * ```
 */
const isNil = value => value == undefined;

/**
 * @remarks {@link isNotNil isNotNil()} checks if a given value is undefined or null.
 *
 * @param value - The value to check.
 *
 * @returns Whether or not the value is nil.
 *
 * @example
 *
 * ```ts
 * isNotNil(undefined); // false
 * isNotNil(null); // false
 * isNotNil('foo'); // true
 * isNotNil(123); // true
 * ```
 */
const isNotNil = value => value != null;

/**
 * @remarks {@link createEmitter} provides a super flexible api for creating an asynchronous event
 * emitter. All events that are emitted through this api are fired off in the order in which they
 * are called while exposing each method as an aynchronous function.
 *
 * @param {Object} config - An object of functions. Also, optionally takes a special `initialize()`
 * function that will execute once before the first interaction with the emitter has resolved.
 *
 * @returns An object with the same functions you passed into the config object with the addition
 * of `subscribe()` and `initialized()` methods. The only difference is that all the methods you
 * passed into the config object are now asynchronous.
 *
 * @example
 *
 * const logger = createEmitter({
 *   error: message => ({
 *     level: 'error',
 *     message,
 *     timestamp: Date.now()
 *   }),
 * });
 *
 * logger.subscribe({
 *   error: ({ level, ...log }) => console[log.level](log),
 * });
 *
 * logger.error(':(');
 */

function createEmitter(config) {
  const INITIALIZE_KEY = 'initialize';
  const InitializeError = new Error(`${INITIALIZE_KEY}() can only be called once.`);
  const queue = [];
  const subscriptions = {};
  let enabled = true;
  let flushing = false;
  let initialized = false;
  async function dequeue() {
    if (queue.length === 0) {
      flushing = false;
      return;
    }
    const fn = queue.shift();
    await fn?.();
    dequeue();
  }
  function createMethod(key) {
    if (!isAsyncFunction(config[key])) {
      return function executeSyncMethod(...args) {
        try {
          if (key === INITIALIZE_KEY) {
            if (initialized) {
              throw InitializeError;
            } else {
              initialized = true;
            }
          } else {
            if (isUndefined(config.initialize)) {
              initialized = true;
            }
          }
          const fn = config[key];
          const result = fn(...args);
          if (enabled) {
            for (const symbol of Object.getOwnPropertySymbols(subscriptions)) {
              try {
                const subscription = subscriptions[symbol];
                subscription[key]?.(result, ...args);
                subscription.all?.(key, result, ...args);
              } catch {}
            }
          }
          return result;
        } catch (error) {
          for (const symbol of Object.getOwnPropertySymbols(subscriptions)) {
            try {
              const subscription = subscriptions[symbol];
              subscription.catch?.(key, error, ...args);
            } catch {}
          }
          throw error;
        }
      };
    }
    return async function enqueueAsyncMethod(...args) {
      return new Promise((resolve, reject) => {
        async function settle() {
          try {
            const fn = config[key];
            const result = await fn(...args);
            if (enabled) {
              for (const symbol of Object.getOwnPropertySymbols(subscriptions)) {
                const subscription = subscriptions[symbol];
                try {
                  await Promise.allSettled([subscription[key]?.(result, ...args), subscription.all?.(key, result, ...args)]);
                } catch {}
              }
            }
            resolve(result);
          } catch (error) {
            for (const symbol of Object.getOwnPropertySymbols(subscriptions)) {
              try {
                const subscription = subscriptions[symbol];
                await subscription.catch?.(key, error, ...args);
              } catch {}
            }
            reject(error);
          }
        }
        if (key === INITIALIZE_KEY) {
          if (initialized) {
            reject(InitializeError);
            return;
          } else {
            initialized = true;
            queue.unshift(settle);
          }
        } else {
          if (isUndefined(config.initialize)) {
            initialized = true;
          }
          queue.push(settle);
        }
        if (!initialized || flushing) {
          return;
        }
        flushing = true;
        dequeue();
      });
    };
  }
  const properties = Object.keys(config).reduce((accumulator, key) => ({
    ...accumulator,
    [key]: isFunction(config[key]) ? createMethod(key) : config[key]
  }), {});
  return {
    ...properties,
    __SUBSCRIPTIONS__: subscriptions,
    get enabled() {
      return enabled;
    },
    get flushing() {
      return flushing;
    },
    get initialized() {
      return initialized;
    },
    disable() {
      enabled = false;
    },
    enable() {
      enabled = true;
    },
    subscribe(subscription) {
      const key = Symbol(Date.now());
      subscriptions[key] = subscription;
      return function unsubscribe() {
        if (flushing) {
          queue.push(async () => {
            delete subscriptions[key];
          });
          return;
        }
        delete subscriptions[key];
      };
    }
  };
}

export { isNull as a, isString as b, createEmitter as c, isNil as d, isArray as e, isError as f, isFunction as g, isNotNil as h, isUndefined as i, isNumber as j, isObject as k, isPromise as l, isRegexp as m, isSymbol as n, typeOf as t };
